import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Check, ArrowRight, Users, Calendar, TrendingUp, Award, Megaphone, Globe,
  ChevronDown, Star, Sparkles
} from 'lucide-react';
import LeadershipSignature from '../components/LeadershipSignature';
import { PlanActionButton } from '../components/PlanActionButton';
import { MembershipRelationsCard } from '../components/MembershipRelationsCard';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembershipPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API}/membership-plans`);
        setPlans(res.data);
      } catch { /* */ } finally { setLoading(false); }
    };
    fetchPlans();
  }, []);

  const staticBenefits = [
    { icon: Users, title: 'Networking Opportunities', description: 'Access exclusive networking events and connect with fellow Dominican entrepreneurs and professionals.' },
    { icon: TrendingUp, title: 'Business Growth', description: 'Get featured in our member directory and gain visibility among potential clients and partners.' },
    { icon: Calendar, title: 'Exclusive Events', description: 'Attend workshops, seminars, and our prestigious annual gala at discounted or free rates.' },
    { icon: Megaphone, title: 'Marketing Exposure', description: 'Promote your business through our social media channels, newsletter, and website.' },
    { icon: Award, title: 'Recognition', description: 'Receive community recognition and access to member awards and spotlights.' },
    { icon: Globe, title: 'Strategic Partnerships', description: 'Connect with businesses in Florida and the Dominican Republic.' },
  ];

  const faqs = [
    { question: 'How do I join?', answer: 'Choose the membership plan that fits your needs and contact us at info@dominicanchamberfl.org or call 1-866-310-3380. You can also register through our member portal.' },
    { question: 'Can I upgrade my plan later?', answer: 'Yes! You can upgrade your membership at any time. Contact the membership team and they will help you transition to a higher tier.' },
    { question: 'What are the payment options?', answer: 'We accept credit cards, bank transfers, and check payments. Annual plans can be paid in full or arranged in installments — contact us for details.' },
    { question: 'Is there a trial period?', answer: 'While we don\'t offer a trial, you\'re welcome to attend one event as a guest before deciding. Contact us to arrange a visit.' },
    { question: 'Can my membership include multiple people?', answer: 'Yes! Our Premier plan includes 2 business representatives, and our Executive plan includes up to 3. Additional representatives can be added to any plan for a supplemental fee.' },
  ];

  return (
    <div data-testid="membership-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#002249] via-[#1D3557] to-[#457B9D] text-white pt-24 sm:pt-32 pb-14 sm:pb-20 px-5 sm:px-8">
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle at 10% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 70%, #E63946 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
        <div className="relative max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[10.5px] sm:text-xs font-bold text-amber-300 tracking-[0.18em] uppercase mb-3 block">DCCF Membership</span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-5 tracking-tight leading-[1.05]">Membership</h1>
            <p className="text-white/85 text-base sm:text-lg lg:text-xl font-light max-w-2xl mx-auto leading-relaxed">
              <span className="text-amber-300 font-semibold">Join the network.</span>
              <span className="text-white/70"> </span>
              <span className="text-amber-300 font-semibold">Unlock opportunities.</span>
            </p>
            <p className="text-white/65 text-sm sm:text-base mt-3 max-w-xl mx-auto leading-relaxed">
              Connect with Dominican-American business leaders across Florida and the Dominican Republic, gain visibility, and access exclusive events and partnerships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Get — Benefits FIRST */}
      <section className="py-14 sm:py-20 px-5 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12 sm:mb-14">
            <span className="text-[10.5px] sm:text-xs font-bold text-[#E63946] tracking-[0.18em] uppercase mb-3 block">What You Get</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] mb-3 tracking-tight">Built for ambitious businesses</h2>
            <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">Every membership unlocks networking, visibility, and growth tools that compound month after month.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {staticBenefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: 0.05 * i }}
                whileHover={{ y: -4 }}
                className="p-6 bg-white border border-slate-200 rounded-2xl hover:shadow-[0_12px_36px_-12px_rgba(0,34,73,0.18)] hover:border-[#002249]/20 transition-shadow group cursor-default"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#002249] to-[#0a3570] rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform shadow-[0_4px_12px_-4px_rgba(0,34,73,0.4)]">
                  <b.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-[#002249] text-[15.5px] mb-1.5">{b.title}</h3>
                <p className="text-[13px] text-slate-600 leading-relaxed">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Plans */}
      <section className="py-14 sm:py-20 px-5 sm:px-8 bg-gradient-to-b from-[#F5F6FA] to-white" data-testid="plans-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12 sm:mb-14">
            <span className="text-[10.5px] sm:text-xs font-bold text-[#E63946] tracking-[0.18em] uppercase mb-3 block">Plans</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] mb-3 tracking-tight">Choose Your Membership</h2>
            <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">Find the plan that fits your business goals — every plan includes the member portal, the directory, and the community.</p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-0 xl:gap-0 max-w-[1100px] mx-auto">
              {plans.map((plan, i) => {
                const tierColors = {
                  hybrid: { accent: '#B87333', light: '#FDF5EE' },
                  essential: { accent: '#78909C', light: '#F5F7F9' },
                  premier: { accent: '#D4A853', light: '#FFFCF0' },
                  executive: { accent: '#4A9BC5', light: '#F0F8FC' },
                };
                const planKey = (plan.name || '').toLowerCase().includes('executive') ? 'executive'
                  : (plan.name || '').toLowerCase().includes('premier') ? 'premier'
                  : (plan.name || '').toLowerCase().includes('essential') ? 'essential' : 'hybrid';
                const colors = tierColors[planKey] || tierColors.hybrid;

                return (
                <motion.div key={plan.id}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: 0.06 * i, ease: [0.2, 0, 0, 1] }}
                  whileHover={{ y: -6 }}
                  className={`bg-white overflow-hidden flex flex-col border border-slate-200 hover:border-[#002249]/25 hover:shadow-[0_18px_40px_-18px_rgba(0,34,73,0.22)] transition-shadow ${i === 0 ? 'rounded-t-xl md:rounded-l-xl md:rounded-tr-none xl:rounded-l-xl xl:rounded-tr-none' : ''} ${i === plans.length - 1 ? 'rounded-b-xl md:rounded-r-xl md:rounded-bl-none xl:rounded-r-xl xl:rounded-bl-none' : ''} ${plan.is_featured ? 'relative z-10 xl:-my-4 xl:shadow-[0_24px_48px_-20px_rgba(0,34,73,0.28)] xl:border-2 xl:rounded-xl' : ''}`}
                  style={plan.is_featured ? { borderColor: colors.accent } : {}}
                  data-testid={`plan-card-${i}`}
                >
                  {plan.is_featured && (
                    <div className="text-white text-center text-[11px] font-bold py-1.5 tracking-wide" style={{ backgroundColor: colors.accent }}>
                      MOST POPULAR
                    </div>
                  )}
                  <div className="p-5 lg:p-6 flex-1 flex flex-col">
                    {/* Tier indicator */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.accent }} />
                      <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: colors.accent }}>{planKey}</span>
                    </div>

                    {/* Plan name + description */}
                    <h3 className="text-lg font-bold text-[#002249] leading-tight">{plan.name}</h3>
                    {plan.name_es && <p className="text-[11px] text-slate-400 italic mt-0.5">{plan.name_es}</p>}
                    {plan.short_description && <p className="text-[13px] text-slate-500 mt-2 leading-relaxed line-clamp-2">{plan.short_description}</p>}

                    {/* Reps */}
                    {plan.reps_included > 1 && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mt-4 mb-2 text-[12px] text-blue-700 flex items-center">
                        <Users className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />{plan.reps_included} representatives included
                      </div>
                    )}

                    {/* Benefits — VALUE FIRST */}
                    {plan.benefit_categories?.length > 0 && (
                      <div className="space-y-3 mt-4 mb-4 flex-1">
                        {plan.benefit_categories.map((cat, ci) => (
                          <div key={ci}>
                            <p className="text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide">{cat.category_name}</p>
                            <ul className="space-y-1.5">
                              {cat.items?.map((item, ii) => (
                                <li key={ii} className="flex items-start text-[12px] text-slate-600 leading-relaxed">
                                  <Check className="w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0" style={{ color: colors.accent }} />{item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {plan.best_for && (
                      <div className="bg-slate-50 rounded-lg p-3 mb-4 text-[12px] text-slate-600 leading-relaxed">
                        <span className="font-semibold text-slate-700">Best for: </span>{plan.best_for}
                      </div>
                    )}

                    {plan.investment_note && (
                      <p className="text-[12px] font-medium mb-3 flex items-center" style={{ color: colors.accent }}><Sparkles className="w-3 h-3 mr-1" />{plan.investment_note}</p>
                    )}

                    {/* PRICE — value-after positioning */}
                    <div className="mt-1 mb-2 py-3 rounded-xl" style={{ backgroundColor: colors.light }}>
                      <p className="text-[10.5px] uppercase font-bold tracking-[0.14em] text-center mb-1" style={{ color: colors.accent }}>Investment</p>
                      <p className="text-2xl lg:text-3xl font-bold text-[#002249] text-center leading-none">{plan.price}</p>
                      {plan.price_note && <p className="text-[11px] text-slate-500 text-center mt-1">{plan.price_note}</p>}
                      {plan.validity_text && <p className="text-[11px] font-medium text-center mt-0.5" style={{ color: colors.accent }}>{plan.validity_text}</p>}
                    </div>
                  </div>

                  {/* CTA — aligned at bottom, dynamic action based on plan.action_type */}
                  <div className="px-5 lg:px-6 pb-5 lg:pb-6 mt-auto">
                    <PlanActionButton
                      plan={plan}
                      style={plan.is_featured ? { backgroundColor: colors.accent } : { backgroundColor: '#002249' }}
                      testIdIndex={i}
                    />
                  </div>
                </motion.div>
              );})}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel />

      {/* Membership Relations */}
      <section className="py-10 sm:py-14 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <MembershipRelationsCard variant="inverse" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#002249] mb-3">Frequently Asked Questions</h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-xl px-5 sm:px-6">
                <AccordionTrigger className="text-sm font-semibold text-[#002249] hover:no-underline">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 leading-relaxed">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Leadership Signature — Memberships Director */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <LeadershipSignature title="Board Director of Memberships" label="Membership Team" blurb="Have questions about membership plans, benefits, or how to join? Connect directly with our Memberships Director." />
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-gradient-to-r from-[#E63946] to-[#1D3557]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to Join?</h2>
          <p className="text-white/80 text-sm sm:text-base mb-8 max-w-xl mx-auto leading-relaxed">
            Contact our membership team to get started or sign up directly through our portal.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link to="/member-login" className="w-full sm:w-auto"><Button size="lg" className="w-full sm:w-auto bg-white text-[#002249] hover:bg-white/90 rounded-full px-8">Apply for Membership <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/contact" className="w-full sm:w-auto"><Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 rounded-full px-8">Contact Us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembershipPage;
